import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { AccountMarketplace, MatchType, StrategyAsin, Targeting } from "@front/m19-api-client";
import { Catalog, CatalogBrandEx } from "@front/m19-models";
import { AccountSelectionService, AsinService, CatalogBrandService } from "@front/m19-services";
import { IButtonDirective, ISelectMenuImports, MODAL_DATA, ModalRef } from "@front/m19-ui";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, switchMap } from "rxjs";

export interface SpGroupTargetings {
  keywords: Targeting[];
  products: StrategyAsin[];
}

export interface FromBrandSelectionModalData {
  matchType?: MatchType;
}

@UntilDestroy()
@Component({
  selector: "app-from-brand-selection-modal",
  imports: [ISelectMenuImports, IButtonDirective],
  templateUrl: "./from-brand-selection-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FromBrandSelectionModalComponent implements OnInit {
  private catalogService = inject(CatalogBrandService);
  private accountService = inject(AccountSelectionService);
  private asinService = inject(AsinService);

  protected readonly modalData = inject(MODAL_DATA) as FromBrandSelectionModalData;
  modalRef = inject(ModalRef);

  private keywordsByBrand: Map<string, Set<string>> = new Map();
  private productsByBrand: Map<string, Set<string>> = new Map();
  protected readonly brands = signal<CatalogBrandEx[]>([]);
  protected readonly selectedBrand = signal<CatalogBrandEx | undefined>(undefined);
  protected readonly selectedTargetings = signal<SpGroupTargetings | undefined>(undefined);

  ngOnInit(): void {
    this.accountService.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am: AccountMarketplace) =>
          combineLatest([
            this.catalogService.getCatalogBrands(am.accountId, am.marketplace),
            this.asinService.getCatalog(am.accountId, am.marketplace),
          ]),
        ),
      )
      .subscribe(([brands, catalog]: [CatalogBrandEx[], Catalog]) => {
        this.keywordsByBrand = this.getKeywordsByBrand(brands);
        this.productsByBrand = this.getProductsByBrand(brands, catalog);
        this.brands.set(brands);
      });
  }

  private getKeywordsByBrand(brands: CatalogBrandEx[]): Map<string, Set<string>> {
    const keywords: Map<string, Set<string>> = new Map();
    for (const b of brands) {
      const existingKeywords = keywords.get(b.brandAlias!) || new Set<string>();
      b.extraSegments?.forEach((s) => {
        existingKeywords.add(s.keyword!);
      });
      existingKeywords.add(b.brandAlias!);
      existingKeywords.add(b.brandName!);
      keywords.set(b.brandAlias!, existingKeywords);

      if (b.parentBrandAlias !== b.brandAlias) {
        const existingKeywords = keywords.get(b.parentBrandAlias!) || new Set<string>();
        const brandAliasKw = keywords.get(b.brandAlias!);
        brandAliasKw?.forEach((kw) => existingKeywords.add(kw));
        keywords.set(b.parentBrandAlias!, existingKeywords);
      }
    }
    return keywords;
  }

  private getProductsByBrand(brands: CatalogBrandEx[], catalog: Catalog): Map<string, Set<string>> {
    const products: Map<string, Set<string>> = new Map();
    for (const b of brands) {
      const existingProducts = products.get(b.brandAlias!) || new Set<string>();
      const asins = catalog.products.filter((a) => a.brand == b.brandName).map((a) => a.asin!);
      asins.forEach((a) => existingProducts.add(a));
      products.set(b.brandAlias!, existingProducts);

      if (b.parentBrandAlias !== b.brandAlias) {
        const existingProducts = products.get(b.parentBrandAlias!) || new Set<string>();
        asins.forEach((a) => existingProducts.add(a));
        products.set(b.parentBrandAlias!, existingProducts);
      }
    }
    return products;
  }

  selectBrand(brand: CatalogBrandEx | undefined) {
    if (!brand) return;
    const keywords = this.keywordsByBrand.get(brand.brandAlias!);
    const products = this.productsByBrand.get(brand.brandAlias!);

    this.selectedTargetings.set({
      keywords: Array.from(keywords ?? []).map((k) => ({
        matchType: this.modalData.matchType ?? MatchType.phrase,
        targetingValue: k,
      })),
      products: Array.from(products ?? []).map((p) => ({
        asin: p,
      })),
    });
    this.selectedBrand.set(brand);
  }

  emit() {
    this.modalRef.close(this.selectedTargetings());
  }

  close() {
    this.modalRef.close();
  }
}
